import { useEffect, useMemo, useState } from 'react';
import { useStyles } from './styles';
import { CircularIndeterminate, Title } from '@utriper/core-ui';
import { useTranslation } from 'react-i18next';
import { Box, Grid2, Tab, Tabs } from '@mui/material';
import { useReport } from 'core/contexts/reportsFilter';
import Layout from 'components/Layout';
import AppReport from './components/AppReport';
import HostReport from './components/HostReport';
import BookingTab from './components/BookingTab';
import ReportFilter from './components/ReportFilter';

const ReportsPageContent = () => {
    const classes = useStyles();
    const { t: translate } = useTranslation();

    const VIEW = useMemo(
        () => ({
            APP: 'app',
            HOST: 'host',
        }),
        []
    );

    const DETAIL_VIEW = useMemo(
        () => ({
            OVERVIEW: 'overview',
            BOOKINGS: 'bookings',
        }),
        []
    );

    const { getHostReports, getAppReports, view, setView, hostReports, appReports, loadingHost, loadingApp } = useReport();
    const [detailView, setDetailView] = useState(DETAIL_VIEW.OVERVIEW);

    useEffect(() => {
        const loadReports = async () => {
            if (view === VIEW.HOST) {
                await getHostReports();
            } else {
                await getAppReports();
            }
        };

        loadReports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view]);

    useEffect(() => {
        getAppReports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleReportTypeChange = (event, newValue) => {
        setView(newValue);
    };

    const handleDetailViewChange = (event, newValue) => {
        setDetailView(newValue);
    };

    const bookingsData = useMemo(() => {
        const reports = view === VIEW.HOST ? hostReports : appReports;
        return reports?.data?.bookings ?? reports?.bookings ?? [];
    }, [view, hostReports, appReports]);

    const isDetailLoading = view === VIEW.HOST ? loadingHost : loadingApp;

    return (
        <Layout contentClassName={classes.content}>
            <Grid2 container width="100%" direction="column" justifyContent="center" alignItems="flex-start" gap="16px" sx={{ color: '#131313' }}>
                <Title value={translate('reports.title')} />

                <Box sx={{ width: '100%' }} mb={4}>
                    <Tabs value={view} onChange={handleReportTypeChange} aria-label="report-tabs" className={classes.tab}>
                        <Tab value={VIEW.APP} label="App" sx={{ fontWeight: 'bold' }} />
                        <Tab value={VIEW.HOST} label="Host" sx={{ fontWeight: 'bold' }} />
                    </Tabs>
                </Box>
            </Grid2>

            <Grid2 container display={'flex'} width={'full'} size={12} marginBlock={1}>
                <ReportFilter />
            </Grid2>

            <Grid2 container width="100%" justifyContent="flex-start" alignItems="center" mb={2}>
                <Tabs value={detailView} onChange={handleDetailViewChange} aria-label="report-detail-tabs" className={classes.tab}>
                    <Tab value={DETAIL_VIEW.OVERVIEW} label={translate('reports.overview_tab')} sx={{ fontWeight: 'bold' }} />
                    <Tab value={DETAIL_VIEW.BOOKINGS} label={translate('reports.bookings_tab')} sx={{ fontWeight: 'bold' }} />
                </Tabs>
            </Grid2>

            {detailView === DETAIL_VIEW.OVERVIEW && (view === VIEW.HOST ? <HostReport /> : <AppReport />)}
            {detailView === DETAIL_VIEW.BOOKINGS &&
                (isDetailLoading ? (
                    <Box width="100%" display="flex" justifyContent="center" py={4}>
                        <CircularIndeterminate />
                    </Box>
                ) : (
                    <BookingTab bookings={bookingsData} />
                ))}
        </Layout>
    );
};

const ReportsPage = () => {
    return <ReportsPageContent />;
};

ReportsPage.displayName = 'ReportsPage';

export default ReportsPage;
